<template>
  <div class="vendor_credit-main glass-tab-page">
    <div class="vendor_credit-header-bar">
      <BrucdcumHeader
        :breadcrumb="['Vendor', 'New Vendor Credit']"
        :back-fallback="vendorsDashboardPath"
      />
    </div>
    <form class="vendor_credit-form" @submit.prevent>
      <div class="glass-tab-view glass-tab-view--flush vendor_credit-glass-shell">
        <div class="glass-tab-view__tabs">
          <TabNavigation v-model="activeTab" :tabs="tabs" @update:modelValue="handleTabChange" />
        </div>

        <div class="glass-tab-view__content vendor_credit-tab-content">
          <div v-if="activeTab === 'basic'" class="glass-tab-view__panel vendor_credit-tab-panel">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows vendor_credit-section">
              <div class="vendor_credit-invoice-meta-row">
                <div class="vendor_credit-form-group vendor_credit-form-group--label-input vendor_credit-meta-field">
                  <LabelInputField
                    input-id="vendor-credit-note-no"
                    label="Credit Note No"
                    v-model="creditNoteNo"
                    placeholder="No"
                    flex
                  />
                </div>
                <div class="vendor_credit-form-group vendor_credit-form-group--label-date vendor_credit-meta-field">
                  <LabelDateField
                    input-id="vendor-credit-date"
                    label="Date"
                    v-model="dateUI"
                    flex
                    @change="onDateChange"
                  />
                </div>
              </div>

              <div class="vendor_credit-form-row vendor_credit-form-row--columns glass-tab-panel__row">
                <div class="vendor_credit-form-col">
                  <div class="vendor_credit-form-group vendor_credit-form-group--label-dropdown vendor_credit-required">
                    <LabelDropdown
                      label="Vendor Name"
                      select-id="vendor-credit-vendor"
                      v-model="vendorId"
                      flex
                      wide
                      required
                      searchable
                      :options="vendorDropdownOptions"
                      :has-error="!!validationErrors.vendorId"
                      @change="clearFieldError('vendorId')"
                    >
                      <div v-if="validationErrors.vendorId" class="vendor_credit-error-text">{{ validationErrors.vendorId }}</div>
                    </LabelDropdown>
                  </div>
                  <div class="vendor_credit-form-group vendor_credit-form-group--label-dropdown">
                    <LabelDropdown
                      label="Contact Name"
                      select-id="vendor-credit-contact"
                      v-model="contactName"
                      flex
                      wide
                      searchable
                      :options="contactDropdownOptions"
                    />
                  </div>
                  <div class="vendor_credit-form-group vendor_credit-form-group--label-input">
                    <LabelInputField
                      input-id="vendor-credit-phone"
                      label="Phone"
                      v-model="phone"
                      placeholder="Phone"
                      flex
                    />
                  </div>
                </div>
                <div class="vendor_credit-form-col">
                  <div class="vendor_credit-form-group vendor_credit-form-group--label-dropdown">
                    <LabelDropdown
                      label="Bank Account"
                      select-id="vendor-credit-bank-account"
                      v-model="bankAccount"
                      flex
                      wide
                      searchable
                      :options="bankAccountDropdownOptions"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'lines'" class="glass-tab-view__panel vendor_credit-tab-panel">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows vendor_credit-section">
              <div class="vendor_credit-itemize-section glass-tab-panel--nested">
                <div
                  class="vendor_credit-itemize-header glass-tab-panel__title"
                  @click="showAccountItems = !showAccountItems"
                >
                  <span v-if="showAccountItems">&#9660;</span><span v-else>&#9654;</span>
                  Itemize by Account
                </div>
                <template v-if="showAccountItems">
                  <div class="consultant-glass-table-section vendor_credit-table-section">
                    <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                      <div class="consultant-glass-table-scroll-x">
                        <table class="consultant-glass-table vendor_credit-account-table vendor_credit-itemize-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                              <th>Amount(USD)</th>
                              <th>Total</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, idx) in accountRows" :key="'account-' + idx" class="consultant-glass-table-row">
                              <td>
                                <select v-model="row.accountId" class="vendor_credit-form-control">
                                  <option value="">Select</option>
                                  <option v-for="account in accountList" :key="account.id" :value="String(account.id)">
                                    {{ account.accountNumber ? account.accountNumber + ' - ' : '' }}{{ account.accountName }}{{ account.accountType ? ' - ' + account.accountType : '' }}
                                  </option>
                                </select>
                              </td>
                              <td><input v-model="row.description" type="text" /></td>
                              <td><input v-model="row.amount" type="number" min="0" step="any" /></td>
                              <td>{{ row.amount !== '' && row.amount != null ? Number(row.amount).toFixed(2) : '' }}</td>
                              <td class="vendor_credit-table-actions">
                                <SecondaryButton
                                  label="Delete"
                                  icon="bi bi-trash"
                                  custom-class="consultant-secondary-btn--danger"
                                  @click="removeAccountRow(idx)"
                                />
                              </td>
                            </tr>
                            <tr class="vendor_credit-table-total-row">
                              <td colspan="5">
                                Total (USD): <span class="vendor_credit-total-value">$ {{ accountTotal.toFixed(2) }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="vendor_credit-itemize-add-wrap">
                    <PrimaryButton label="Add New Account" icon="bi bi-plus-lg" @click="addAccountRow" />
                  </div>
                </template>
              </div>

              <div class="vendor_credit-itemize-section glass-tab-panel--nested">
                <div
                  class="vendor_credit-itemize-header glass-tab-panel__title"
                  @click="showProductItems = !showProductItems"
                >
                  <span v-if="showProductItems">&#9660;</span><span v-else>&#9654;</span>
                  Itemize by Product/Service
                </div>
                <template v-if="showProductItems">
                  <div class="consultant-glass-table-section vendor_credit-table-section">
                    <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                      <div class="consultant-glass-table-scroll-x">
                        <table class="consultant-glass-table vendor_credit-product-table vendor_credit-itemize-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                              <th>Quantity</th>
                              <th>Unit Price</th>
                              <th>Total</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, idx) in productRows" :key="'product-' + idx" class="consultant-glass-table-row">
                              <td>
                                <select v-model="row.selectedItem" class="vendor_credit-form-control" @change="onProductServiceChange(row)">
                                  <option value="">Select</option>
                                  <option
                                    v-for="item in availableProductServiceItems(idx)"
                                    :key="item.type + '-' + item.id"
                                    :value="JSON.stringify({ id: item.id, type: item.type })"
                                  >
                                    {{ item.name }} - {{ item.type }}
                                  </option>
                                </select>
                              </td>
                              <td><input v-model="row.description" type="text" /></td>
                              <td><input v-model="row.quantity" type="number" min="0" step="any" /></td>
                              <td><input v-model="row.unitPrice" type="number" min="0" step="any" /></td>
                              <td>
                                {{ row.quantity && row.unitPrice ? (Number(row.quantity) * Number(row.unitPrice)).toFixed(2) : '' }}
                              </td>
                              <td class="vendor_credit-table-actions">
                                <SecondaryButton
                                  label="Delete"
                                  icon="bi bi-trash"
                                  custom-class="consultant-secondary-btn--danger"
                                  @click="removeProductRow(idx)"
                                />
                              </td>
                            </tr>
                            <tr class="vendor_credit-table-total-row">
                              <td colspan="6">
                                Total (USD): <span class="vendor_credit-total-value">$ {{ productServiceTotal.toFixed(2) }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="vendor_credit-itemize-add-wrap">
                    <PrimaryButton label="Add New Item" icon="bi bi-plus-lg" @click="addProductRow" />
                  </div>
                </template>
              </div>

              <div class="vendor_credit-form-group vendor_credit-form-group--label-description">
                <LabelDescriptionField
                  input-id="vendor-credit-comments"
                  label="Write comments for this Vendor Credit."
                  v-model="comments"
                  placeholder="Comments"
                  flex
                />
              </div>

              <div class="vendor_credit-total-section glass-tab-panel--nested">
                <span>Total (USD)</span>
                <span class="vendor_credit-total-value">$ {{ finalTotal.toFixed(2) }}</span>
              </div>

              <div class="vendor_credit-secondary-actions">
                <SecondaryButton label="Make it recurring" icon="bi bi-arrow-repeat" @click="makeRecurring" />
                <SecondaryButton label="Save as draft" icon="bi bi-file-earmark" @click="saveAsDraft" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="vendor_credit-form-actions glass-tab-view__actions">
        <template v-if="activeTab === 'lines'">
          <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
          <PrimaryButton label="Save and New" icon="bi bi-plus-lg" @click="saveAndNew" />
          <PrimaryButton label="Save and Close" icon="bi bi-check-lg" @click="saveAndClose" />
        </template>
        <template v-else>
          <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
          <PrimaryButton label="Next" icon="bi bi-arrow-right" @click="handleNext" />
        </template>
      </div>
    </form>

    <ErrorModal :show="showErrorModal" title="Error" :message="errorMessage" @close="closeErrorModal" />
  </div>
</template>

<script>
import '../styles/VendorCredit.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import ApiService from '@/services/api.service.js';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'VendorCredit',
  components: {
    BrucdcumHeader,
    TabNavigation,
    ErrorModal,
    LabelInputField,
    LabelDropdown,
    LabelDescriptionField,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    const today = new Date().toISOString().split('T')[0];
    return {
      tabs: [
        { id: 'basic', name: 'Basic Information' },
        { id: 'lines', name: 'Lines & Totals' },
      ],
      activeTab: 'basic',
      validationErrors: {},
      showErrorModal: false,
      errorMessage: '',
      vendorId: '',
      contactName: '',
      phone: '',
      vendorList: [],
      contactList: [],
      date: today,
      creditNoteNo: '',
      comments: '',
      showAccountItems: true,
      showProductItems: true,
      accountRows: [{ accountId: '', description: '', amount: 0 }],
      productRows: [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }],
      accountList: [],
      bankAccountList: [],
      bankAccount: '',
      productList: [],
      serviceList: [],
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id;
    },
    vendorsDashboardPath() {
      return `/company/${this.companyId}/dashboard/vendors`;
    },
    dateUI: {
      get() {
        return this.formatDateForUI(this.date);
      },
      set(val) {
        this.onDateChange(val);
      },
    },
    productServiceList() {
      return [...this.productList, ...this.serviceList];
    },
    productServiceTotal() {
      return this.productRows.reduce(
        (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0),
        0,
      );
    },
    accountTotal() {
      return this.accountRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    },
    finalTotal() {
      return this.accountTotal + this.productServiceTotal;
    },
    vendorDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.vendorList || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.vendorName,
        })),
      ];
    },
    contactDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.contactList || []).map((contact) => ({
          value: contact.name,
          label: `${contact.name}${contact.email ? ` (${contact.email})` : ''}${contact.primary ? ' [Primary]' : ''}`,
        })),
      ];
    },
    bankAccountDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.bankAccountList || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber ? acc.accountNumber + ' - ' : ''}${acc.accountName}${acc.accountType ? ' - ' + acc.accountType : ''}`,
        })),
      ];
    },
  },
  watch: {
    vendorId(val) {
      const selected = this.vendorList.find((v) => String(v.id) === String(val));
      if (selected) {
        this.contactList = selected.contacts || [];
        this.contactName = '';
        this.phone = '';
      } else {
        this.contactList = [];
        this.contactName = '';
        this.phone = '';
      }
    },
    contactName(val) {
      const selectedContact = this.contactList.find((c) => c.name === val);
      this.phone = selectedContact ? selectedContact.phone || '' : '';
    },
  },
  async created() {
    try {
      const data = await ApiService.getAllVendors();
      this.vendorList = Array.isArray(data) ? data : [];
    } catch (err) {
      this.vendorList = [];
    }
    try {
      const res = await ApiService.getCompanyAccounts(this.companyId);
      const allAccounts = res.data.accounts || res.data || [];
      this.bankAccountList = allAccounts.filter((a) => ['Bank', 'Cash', 'Paypal'].includes(a.accountType));
      this.accountList = allAccounts.filter((a) =>
        ['Expense', 'Other Expense', 'Cost of Goods Sold', 'Other current Asset', 'Fixed Asset'].includes(a.accountType),
      );
      const productRes = await ApiService.get(`/vendor/product-items/company/${this.companyId}`);
      this.productList = (productRes.data || []).map((item) => ({
        id: item.id,
        name: item.productName,
        type: item.type,
        salesPrice: item.salesPrice,
      }));
      const serviceRes = await ApiService.get(`/vendor/service-items/company/${this.companyId}`);
      this.serviceList = (serviceRes.data || []).map((item) => ({
        id: item.id,
        name: item.serviceName,
        type: item.type,
        salesPrice: item.salesPrice,
      }));
    } catch (e) {
      this.bankAccountList = [];
      this.accountList = [];
      this.productList = [];
      this.serviceList = [];
    }
  },
  methods: {
    formatDateForUI(isoDate) {
      if (!isoDate) return '';
      const parts = String(isoDate).split('-');
      if (parts.length !== 3) return isoDate;
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    },
    parseDateFromUI(value) {
      if (!value) return '';
      const parts = String(value).split('/');
      if (parts.length !== 3) return value;
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
    onDateChange(value) {
      this.date = this.parseDateFromUI(value);
    },
    handleTabChange() {},
    clearFieldError(field) {
      if (this.validationErrors[field]) {
        const next = { ...this.validationErrors };
        delete next[field];
        this.validationErrors = next;
      }
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
    showValidationErrors(errors) {
      this.validationErrors = errors;
      const messages = Object.values(errors)
        .map((m) => `• ${m}`)
        .join('\n');
      this.errorMessage = `Please fix the following issues:\n\n${messages}`;
      this.showErrorModal = true;
    },
    validateCurrentTab(tabId = this.activeTab) {
      const errors = {};
      if (tabId === 'basic') {
        if (!this.vendorId) {
          errors.vendorId = 'Vendor is required.';
        }
      }
      return errors;
    },
    validateAll() {
      const all = {
        ...this.validateCurrentTab('basic'),
        ...this.validateCurrentTab('lines'),
      };
      this.validationErrors = all;
      if (Object.keys(all).length > 0) {
        this.showValidationErrors(all);
        return false;
      }
      return true;
    },
    handleNext() {
      const err = this.validateCurrentTab('basic');
      if (Object.keys(err).length > 0) {
        this.validationErrors = { ...this.validationErrors, ...err };
        this.showValidationErrors(err);
        return;
      }
      this.validationErrors = {};
      this.activeTab = 'lines';
    },
    cancel() {
      this.$router.push(this.vendorsDashboardPath);
    },
    async saveAndClose() {
      if (!this.validateAll()) return;
      try {
        await this.saveCredit();
        if (this.$toast) this.$toast.success('Vendor credit created successfully!');
        this.$router.push(this.vendorsDashboardPath);
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Failed to save';
        if (this.$toast) this.$toast.error(msg);
        else {
          this.errorMessage = msg;
          this.showErrorModal = true;
        }
      }
    },
    async saveAndNew() {
      if (!this.validateAll()) return;
      try {
        await this.saveCredit();
        if (this.$toast) this.$toast.success('Vendor credit created successfully!');
        this.resetForm();
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Failed to save';
        if (this.$toast) this.$toast.error(msg);
        else {
          this.errorMessage = msg;
          this.showErrorModal = true;
        }
      }
    },
    addAccountRow() {
      this.accountRows.push({ accountId: '', description: '', amount: 0 });
    },
    removeAccountRow(idx) {
      this.accountRows.splice(idx, 1);
    },
    addProductRow() {
      this.productRows.push({ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 });
    },
    removeProductRow(idx) {
      this.productRows.splice(idx, 1);
    },
    availableProductServiceItems(currentIdx) {
      const selectedIds = this.productRows
        .filter((row, idx) => idx !== currentIdx)
        .map((row) => `${row.itemId}-${row.itemType}`);
      return this.productServiceList.filter((item) => !selectedIds.includes(`${item.id}-${item.type}`));
    },
    onProductServiceChange(row) {
      if (typeof row.selectedItem === 'string') {
        try {
          const parsed = JSON.parse(row.selectedItem);
          row.itemId = parsed.id;
          row.itemType = parsed.type;
        } catch (e) {
          row.itemId = '';
          row.itemType = '';
        }
      }
      const item = this.productServiceList.find((i) => i.id === row.itemId && i.type === row.itemType);
      row.unitPrice = item ? Number(item.salesPrice) || 0 : 0;
    },
    async saveCredit() {
      const payload = {
        vendorId: this.vendorId,
        contactName: this.contactName,
        phone: this.phone,
        bankAccount: this.bankAccount,
        date: this.date,
        creditNoteNo: this.creditNoteNo,
        comments: this.comments,
        accountRecords: this.accountRows
          .filter((row) => row.accountId && row.amount)
          .map((row) => ({
            accountId: row.accountId,
            description: row.description,
            amount: row.amount,
          })),
        itemRecords: this.productRows
          .filter((row) => row.itemId && row.itemType)
          .map((row) => ({
            itemId: row.itemId,
            itemType: row.itemType,
            description: row.description,
            quantity: row.quantity,
            unitPrice: row.unitPrice,
          })),
      };
      await ApiService.post('/vendor/vendor-credit', payload);
    },
    resetForm() {
      const today = new Date().toISOString().split('T')[0];
      this.activeTab = 'basic';
      this.validationErrors = {};
      this.vendorId = '';
      this.contactName = '';
      this.phone = '';
      this.date = today;
      this.creditNoteNo = '';
      this.comments = '';
      this.accountRows = [{ accountId: '', description: '', amount: 0 }];
      this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
      this.bankAccount = '';
      this.contactList = [];
    },
    makeRecurring() {
      this.$toast && this.$toast.info('Recurring vendor credit is not implemented yet.');
    },
    saveAsDraft() {
      this.$toast && this.$toast.info('Save as draft is not implemented yet.');
    },
  },
};
</script>
